import { Product, ProductItemWithDetails, ExternalBarcodeLookupResponse } from '../utils/grocyApi.ts';
import { usePurchaseMutation } from './usePurchaseMutation.ts';
import { useUserSettingsQuery } from './useQueries.ts';
import { CreationMode } from '../types/purchase.ts';

interface UsePurchaseLogicProps {
  onComplete: () => void;
}

export function usePurchaseLogic({ onComplete }: UsePurchaseLogicProps) {
  const { purchaseExisting, createAndPurchase, purchaseNew, linkBarcodeAndPurchase, isLoading } = usePurchaseMutation();
  const { data: userSettings } = useUserSettingsQuery();
  const presetQuantityUnitId = Number(userSettings?.product_presets_qu_id) || undefined;
  
  // Common purchase function for manual flow
  const handleManualPurchase = async (
    selectedProduct: Product | null,
    isCreating: boolean,
    newProductName: string,
    newProductGroupId: number | null,
    amount: number,
    expiryDate: string,
    locationId: number | null,
    parentProductId: number | null
  ) => {
    try {
      if (isCreating) {
        if (!newProductName || !locationId) return;
        await createAndPurchase({
          productData: {
            name: newProductName,
            location_id: locationId,
            product_group_id: newProductGroupId || 1,
            qu_id_purchase: presetQuantityUnitId,
            qu_id_stock: presetQuantityUnitId,
          },
          amount,
          expiryDate,
          locationId: locationId,
          parentProductId: parentProductId || undefined,
        });
      } else {
        if (!selectedProduct) return;
        await purchaseExisting({
          productId: selectedProduct.id,
          amount,
          expiryDate,
          locationId: locationId || selectedProduct.location_id,
          currentLocationId: selectedProduct.location_id,
          parentProductId: parentProductId || undefined,
        });
      }
      onComplete();
    } catch (error) {
      console.error('Manual purchase failed:', error);
    }
  };

  // Purchase function for scanned product flow
  const handleScannedPurchase = async (
    existingProduct: ProductItemWithDetails | undefined,
    unknownProduct: ExternalBarcodeLookupResponse | undefined,
    creationMode: CreationMode,
    linkProductId: number | null,
    linkProductLocationId: number | null,
    amount: number,
    expiryDate: string,
    locationId: number | null,
    parentProductId: number | null,
    productGroupId: number | null
  ) => {
    try {
      if (existingProduct) {
        // Purchase existing product
        await purchaseExisting({
          productId: existingProduct.product.id,
          amount,
          expiryDate,
          locationId: locationId || existingProduct.product.location_id,
          currentLocationId: existingProduct.product.location_id,
          parentProductId: parentProductId || undefined,
        });
      } else if (unknownProduct) {
        if (creationMode === CreationMode.Create || creationMode === CreationMode.Parent) {
          // Create new product without parent and purchase
          await purchaseNew({
            barcode: unknownProduct.__barcode,
            amount,
            expiryDate,
            locationId: locationId || unknownProduct.location_id,
            productGroupId: productGroupId || 1,
            parentProductId: parentProductId || undefined,
          });
        } else if (creationMode === CreationMode.Link) {
          if (!linkProductId) return;
          // Link barcode to existing and purchase
          await linkBarcodeAndPurchase({
            productId: linkProductId,
            barcode: unknownProduct.__barcode,
            amount,
            expiryDate,
            locationId: locationId || unknownProduct.location_id,
            currentLocationId: linkProductLocationId || undefined,
          });
        }
      }
      onComplete();
    } catch (error) {
      console.error('Purchase failed:', error);
    }
  };

  return {
    handleManualPurchase,
    handleScannedPurchase,
    isLoading
  };
}
